import { letterboxRatio } from './decode';
import { FRESH_BUDGET, budgetStep, budgetSummary, budgetTick } from './budget';

/*
 * One running camera's line to the detector's worker (detector-worker.js), as frameAnalyser.js is to the frame
 * worker: at most one frame on its way, so a slow phone runs fewer rather than falling behind. Any failure ends it,
 * once, through onState; the camera carries on without it.
 *
 *   onState({state, reason, warmMs}):  loading -> running | too_slow | failed
 *   onBoxes(boxes, summary):           a run inside the budget. A run over it only moves the summary.
 */
export const STALE_MS = 5000;

/** process.env.PITRIOT_ORT_WASM is vite.config.js's: where the runtime's WASM is, and its hash. */
export function runtimeWasm() {
  try {
    return JSON.parse(process.env.PITRIOT_ORT_WASM || 'null');
  } catch {
    return null;
  }
}

export function detectorSupported() {
  return typeof Worker !== 'undefined' && typeof createImageBitmap === 'function'
    && typeof OffscreenCanvas !== 'undefined' && typeof indexedDB !== 'undefined';
}

function makeDetectorWorker() {
  return new Worker(new URL('./detector-worker.js', import.meta.url), { type: 'module', name: 'detector' });
}

const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

export function createDetector({ manifest, wasm = runtimeWasm(), onState, onBoxes, makeWorker = makeDetectorWorker, clock = now }) {
  let worker = null;
  let closed = false;
  let ready = false;
  let sentAt = null;
  let budget = FRESH_BUDGET;

  const close = () => {
    closed = true;
    if (worker && typeof worker.terminate === 'function') worker.terminate();
    worker = null;
  };
  const fail = (reason) => {
    if (closed) return;
    close();
    onState({ state: 'failed', reason });
  };

  if (!wasm) {
    Promise.resolve().then(() => fail('no_runtime'));
    return { send: () => {}, close };
  }

  try {
    worker = makeWorker();
    worker.onmessage = (event) => {
      if (closed) return;
      const { type, reason, boxes, ms, warmMs } = event.data || {};
      if (type === 'failed') {
        fail(reason);
      } else if (type === 'ready') {
        ready = true;
        onState({ state: 'running', warmMs });
      } else if (type === 'boxes') {
        sentAt = null;
        const step = budgetStep(budget, ms);
        budget = step.state;
        onBoxes(step.keep ? boxes : null, budgetSummary(budget));
        if (budget.tooSlow) {
          close();
          onState({ state: 'too_slow' });
        }
      }
    };
    worker.onerror = () => fail('worker_failed');
    worker.onmessageerror = () => fail('worker_failed');
    onState({ state: 'loading' });
    worker.postMessage({ type: 'load', manifest, wasm });
  } catch {
    Promise.resolve().then(() => fail('worker_failed'));
  }

  const send = (video) => {
    if (closed || !worker || !ready) return;
    const t = clock();
    if (sentAt !== null) {
      if (t - sentAt > STALE_MS) fail('stalled');
      return;
    }
    const tick = budgetTick(budget);
    budget = tick.state;
    if (!tick.send) return;
    if (!video || video.readyState < 2 || !video.videoWidth || !video.videoHeight) return;
    const { size } = manifest.input;
    const ratio = letterboxRatio(video.videoWidth, video.videoHeight, size);
    const w = Math.max(1, Math.round(video.videoWidth * ratio));
    const h = Math.max(1, Math.round(video.videoHeight * ratio));
    sentAt = t;
    createImageBitmap(video, { resizeWidth: w, resizeHeight: h, resizeQuality: 'medium' }).then((bitmap) => {
      if (closed || !worker) {
        bitmap.close();
        return;
      }
      worker.postMessage({ type: 'frame', at: t, bitmap, w, h }, [bitmap]);
    }).catch(() => fail('frame_refused'));
  };

  return { send, close };
}
